import type { RapidContact } from '../../entities/rapidContact/rapidContactTypes';
import { MOCK_GROUP_ID } from './mockSite';

/**
 * `personId` è un link di sistema che il DTO REST non espone (a differenza di
 * `imported`, ora campo reale del DTO): lo teniamo come campo MOCK-interno per
 * simulare il pannello Fiere (by-person). Il converter DTO lo ignora.
 */
type MockRapidContact = RapidContact & { personId?: number };

const audit = {
  companyId: 1,
  groupId: MOCK_GROUP_ID,
  userId: 1,
  userName: 'Sistema',
  createDate: '2026-05-15T10:00:00Z',
  modifiedDate: '2026-05-15T10:00:00Z',
  // Stato di follow-up come key della picklist (FILO-67): i record mock non l'hanno.
  followupStatusListTypeEntryKey: null,
  followupStatusName: null,
  // Commerciale di riferimento (FILO-67): l'utente mock 1.
  salespersonUserId: 1,
  salespersonUserName: 'Sistema',
  // Stato di import di default per i record mock: i record già importati (rc13+)
  // lo sovrascrivono a true dopo lo spread di `audit`.
  imported: false,
};

/**
 * 12 record di esempio mix completi/parziali. Replicano i 16 campi della
 * form RapidContactPage. `fairEvent` è obbligatorio (validate backend): tutti
 * i record hanno una fiera.
 * - 4 record con dati fiera completi (interest/interestLevel/followupAction)
 * - 4 record con dati base + azienda
 * - 4 record minimi (firstName + lastName + fiera + azienda; email opzionale)
 *   -> tutti soddisfano i vincoli obbligatori (nome, azienda, fiera): editabili.
 *
 * Le date sono distribuite negli ultimi 60 giorni per testare l'ordinamento
 * di default (createDate desc).
 */
export const mockRapidContacts: MockRapidContact[] = [
  // --- Fiera completi ---
  {
    rapidContactId: 1, firstName: 'Mario', lastName: 'Rossi',
    email: 'mario.rossi@acme.it', phone: '+39 333 1234567', role: 'CEO',
    companyName: 'Acme Srl', companyDomain: 'acme.it',
    companyAddress: 'Via Roma 1, Milano', companyCountry: 'Italia',
    fairEvent: 'SMAU 2026', interest: 'Cloud CRM con integrazione email',
    productCategory: 'Software', interestLevel: 4,
    followupAction: 'Call entro 7gg',
    notes: 'Cliente interessato a demo personalizzata',
    ...audit, createDate: '2026-05-15T10:00:00Z', modifiedDate: '2026-05-15T10:00:00Z',
  },
  {
    rapidContactId: 2, firstName: 'Anna', lastName: 'Verdi',
    email: 'anna.verdi@globex.com', phone: '+39 348 2233445', role: 'CTO',
    companyName: 'Globex Corp', companyDomain: 'globex.com',
    companyAddress: 'Via Garibaldi 12, Roma', companyCountry: 'Italia',
    fairEvent: 'SMAU 2026', interest: 'Automation marketing',
    productCategory: 'SaaS', interestLevel: 5,
    followupAction: 'Invio brochure',
    notes: 'Decision maker tecnico, valuta pilot Q3',
    ...audit, createDate: '2026-05-16T14:20:00Z', modifiedDate: '2026-05-16T14:20:00Z',
  },
  {
    rapidContactId: 3, firstName: 'Hans', lastName: 'Mueller',
    email: 'h.mueller@bauerag.de', phone: '+49 89 11223344', role: 'Sales Director',
    companyName: 'Bauer AG', companyDomain: 'bauerag.de',
    companyAddress: 'Marienplatz 5, Munich', companyCountry: 'Germania',
    fairEvent: 'Hannover Messe 2026', interest: 'Integrazione con SAP',
    productCategory: 'Software', interestLevel: 3,
    followupAction: 'Meeting tecnico',
    notes: 'Aspetta proposta tecnica entro fine mese',
    ...audit, createDate: '2026-04-22T09:00:00Z', modifiedDate: '2026-04-22T09:00:00Z',
  },
  {
    rapidContactId: 4, firstName: 'Sophie', lastName: 'Dubois',
    email: 'sophie.dubois@start-paris.fr', phone: '+33 1 4455 6677', role: 'Founder',
    companyName: 'Start Paris', companyDomain: 'start-paris.fr',
    companyAddress: '15 rue de Rivoli, Paris', companyCountry: 'Francia',
    fairEvent: 'Viva Tech 2026', interest: 'Onboarding rapido per startup',
    productCategory: 'Servizi', interestLevel: 4,
    followupAction: 'Demo personalizzata',
    notes: 'Startup early stage, budget limitato ma scalabilita potenziale',
    ...audit, createDate: '2026-05-08T11:30:00Z', modifiedDate: '2026-05-08T11:30:00Z',
  },
  // --- Base + azienda (no fiera) ---
  {
    rapidContactId: 5, firstName: 'Luca', lastName: 'Bianchi',
    email: 'luca.bianchi@costruzionivalle.it', phone: '+39 0461 778899', role: 'Direttore Operativo',
    companyName: 'Costruzioni Valle Srl', companyDomain: 'costruzionivalle.it',
    companyAddress: 'Via Brennero 100, Trento', companyCountry: 'Italia',
    fairEvent: 'SAIE 2026', interest: 'Modulo gestione cantieri',
    productCategory: 'Software', interestLevel: 3,
    followupAction: 'Preventivo',
    notes: null,
    ...audit, createDate: '2026-05-10T16:45:00Z', modifiedDate: '2026-05-10T16:45:00Z',
  },
  {
    rapidContactId: 6, firstName: 'Elena', lastName: 'Marchetti',
    email: 'e.marchetti@studio-legale-mi.it', phone: '+39 02 11223344', role: 'Partner',
    companyName: 'Studio Legale Marchetti', companyDomain: 'studio-legale-mi.it',
    companyAddress: 'Via Manzoni 25, Milano', companyCountry: 'Italia',
    fairEvent: 'SMAU 2026', interest: 'Gestione documenti e GDPR',
    productCategory: 'Servizi', interestLevel: 2,
    followupAction: null,
    notes: 'Vuole presentazione dopo le ferie estive',
    ...audit, createDate: '2026-05-12T10:15:00Z', modifiedDate: '2026-05-12T10:15:00Z',
  },
  {
    rapidContactId: 7, firstName: 'Roberto', lastName: 'Conti',
    email: 'r.conti@miksi.it', phone: null, role: 'IT Manager',
    companyName: 'Miksi Solutions', companyDomain: 'miksi.it',
    companyAddress: null, companyCountry: 'Italia',
    fairEvent: 'SMAU 2026', interest: 'Migrazione da Salesforce',
    productCategory: 'Software', interestLevel: 4,
    followupAction: 'POC tecnica',
    notes: null,
    ...audit, createDate: '2026-05-05T13:00:00Z', modifiedDate: '2026-05-05T13:00:00Z',
  },
  {
    rapidContactId: 8, firstName: 'Sara', lastName: 'Colombo',
    email: 'sara.colombo@email.it', phone: '+39 035 4455667', role: null,
    companyName: 'Colombo Manufacturing', companyDomain: null,
    companyAddress: 'Via Industriale 5, Bergamo', companyCountry: 'Italia',
    fairEvent: 'MECSPE 2026', interest: null,
    productCategory: 'Manifattura', interestLevel: 2,
    followupAction: null,
    notes: 'Contatto da segnalazione di Visconti',
    ...audit, createDate: '2026-04-28T08:30:00Z', modifiedDate: '2026-04-28T08:30:00Z',
  },
  // --- Minimi ---
  {
    rapidContactId: 9, firstName: 'Giorgio', lastName: 'Ferrari',
    email: 'g.ferrari@gmail.com', phone: null, role: null,
    companyName: 'Ferrari Consulting', companyDomain: null, companyAddress: null, companyCountry: null,
    fairEvent: 'SMAU 2026', interest: null, productCategory: null, interestLevel: 0,
    followupAction: null, notes: null,
    ...audit, createDate: '2026-04-15T17:00:00Z', modifiedDate: '2026-04-15T17:00:00Z',
  },
  {
    rapidContactId: 10, firstName: 'Marta', lastName: 'Fabbri',
    email: null, phone: '+39 339 9988776', role: null,
    companyName: 'Fabbri Group', companyDomain: null, companyAddress: null, companyCountry: null,
    fairEvent: 'SMAU 2026', interest: null, productCategory: null, interestLevel: 0,
    followupAction: null, notes: null,
    ...audit, createDate: '2026-04-10T09:15:00Z', modifiedDate: '2026-04-10T09:15:00Z',
  },
  {
    rapidContactId: 11, firstName: 'Davide', lastName: 'Pellegrini',
    email: 'd.pellegrini@euroimpianti.it', phone: null, role: null,
    companyName: 'EuroImpianti Srl', companyDomain: 'euroimpianti.it', companyAddress: null, companyCountry: null,
    fairEvent: 'MCE 2026', interest: null, productCategory: null, interestLevel: 0,
    followupAction: null, notes: null,
    ...audit, createDate: '2026-04-02T12:00:00Z', modifiedDate: '2026-04-02T12:00:00Z',
  },
  {
    rapidContactId: 12, firstName: 'Claudia', lastName: 'Rizzo',
    email: null, phone: null, role: null,
    companyName: 'Rizzo & Partners', companyDomain: null, companyAddress: null, companyCountry: null,
    fairEvent: 'SMAU 2026', interest: null, productCategory: null, interestLevel: 0,
    followupAction: null, notes: null,
    ...audit, createDate: '2026-03-25T15:30:00Z', modifiedDate: '2026-03-25T15:30:00Z',
  },
  // --- GIÀ IMPORTATI (imported=true + personId): non compaiono nella lista fiera,
  //     ma nel pannello "Fiere" della scheda del Person collegato. ---
  // Stefano Arteconi (Person 19) — due fiere diverse (anni distinti).
  {
    rapidContactId: 13, firstName: 'Stefano', lastName: 'Arteconi',
    email: 'stefano.arteconi@d-vel.com', phone: '+39 329 8267164', role: 'CTO',
    companyName: 'D-Vel', companyDomain: 'd-vel.com',
    companyAddress: 'Trento', companyCountry: 'Italia',
    fairEvent: 'SMAU 2024', interest: 'Piattaforma CRM', productCategory: 'Software',
    interestLevel: 5, followupAction: null,
    notes: 'Acquisito a SMAU 2024',
    ...audit, createDate: '2024-10-22T10:00:00Z', modifiedDate: '2024-10-22T10:00:00Z',
    imported: true, personId: 19,
  },
  {
    rapidContactId: 14, firstName: 'Stefano', lastName: 'Arteconi',
    email: 'stefano.arteconi@d-vel.com', phone: '+39 329 8267164', role: 'CTO',
    companyName: 'D-Vel', companyDomain: 'd-vel.com',
    companyAddress: 'Trento', companyCountry: 'Italia',
    fairEvent: 'Web Summit 2025', interest: 'Automazioni', productCategory: 'Software',
    interestLevel: 4, followupAction: null,
    notes: 'Re-incontrato a Web Summit 2025',
    ...audit, createDate: '2025-11-10T09:30:00Z', modifiedDate: '2025-11-10T09:30:00Z',
    imported: true, personId: 19,
  },
  // Francesca Ruocco (Person 5).
  {
    rapidContactId: 15, firstName: 'Francesca', lastName: 'Ruocco',
    email: 'francescaruocco@trentinosicurezza.it', phone: '+39 06 1234567', role: 'Responsabile',
    companyName: 'Trentino Sicurezza', companyDomain: 'trentinosicurezza.it',
    companyAddress: 'Roma', companyCountry: 'Italia',
    fairEvent: 'Sicurezza Expo 2025', interest: 'Sistemi di sicurezza', productCategory: 'Servizi',
    interestLevel: 4, followupAction: null,
    notes: 'Contatto da Sicurezza Expo 2025',
    ...audit, createDate: '2025-09-18T14:00:00Z', modifiedDate: '2025-09-18T14:00:00Z',
    imported: true, personId: 5,
  },
];
